import { toast } from 'react-hot-toast';
import { formatDate } from 'shared_items/formatConventions';
import { nextRowKey, parseDecimal } from './snapshot_form_shared';

export const RETURN_PAYMENT_TYPES = [
  { value: 'DIVIDENDOS', label: 'Dividendos' },
  { value: 'JURO CAPITAL PROPRIO', label: 'Juro Capital Próprio' },
  { value: 'REND IMOBILIARIO', label: 'Rendimento Imobiliário' },
  { value: 'other', label: 'Other' },
];

export const emptyReturnRow = () => ({
  key: nextRowKey(),
  asset: '',
  payment_type: 'DIVIDENDOS',
  payment_date: '',
  payment_value: '',
  quantity_at_time: '',
  notes: '',
  /** Set by pre-fill only: last return used as template (display-only). */
  refFromLastReturn: null,
});

const toYMD = (s) => {
  const str = String(s || '').trim().slice(0, 10);
  if (str.length !== 10 || str[4] !== '-' || str[7] !== '-') return null;
  return str;
};

/**
 * Uses the calendar month of the latest payment_date across returns, then builds one row per
 * distinct (asset, payment_type) among tradeable assets in that month. Latest row in the month
 * wins for quantity. payment_date and payment_value are left empty on each row.
 * Rows are ordered by payment_date descending (most recent first), then asset_code, then payment_type.
 * @returns {{ rows: object[], monthLabel: string | null }}
 */
export const prefillReturnRowsFromLastMonth = (returns, tradeableAssets) => {
  const tradeableIds = new Set((tradeableAssets || []).map((a) => Number(a.id)));
  const codeById = new Map(
    (tradeableAssets || []).map((a) => [Number(a.id), String(a.asset_code || '').trim()])
  );

  const list = (returns || []).filter((r) => toYMD(r.payment_date));
  if (list.length === 0) {
    return { rows: [], monthLabel: null };
  }

  let latestYmd = null;
  for (const r of list) {
    const ymd = toYMD(r.payment_date);
    if (!latestYmd || ymd > latestYmd) latestYmd = ymd;
  }
  const refYm = latestYmd.slice(0, 7);
  const monthLabel = refYm;

  const inMonth = list.filter((r) => toYMD(r.payment_date).startsWith(refYm));

  const byKey = new Map();
  for (const r of inMonth) {
    const aid = Number(r.asset);
    if (!Number.isFinite(aid) || !tradeableIds.has(aid)) continue;
    const pt = String(r.payment_type || 'other');
    const key = `${aid}\t${pt}`;
    const prev = byKey.get(key);
    const ymd = toYMD(r.payment_date);
    if (!prev) {
      byKey.set(key, r);
      continue;
    }
    const py = toYMD(prev.payment_date);
    if (ymd > py || (ymd === py && Number(r.id) > Number(prev.id))) {
      byKey.set(key, r);
    }
  }

  const sortedRecords = [...byKey.values()].sort((a, b) => {
    const da = toYMD(a.payment_date) || '';
    const db = toYMD(b.payment_date) || '';
    if (da !== db) return db.localeCompare(da);
    const ca = codeById.get(Number(a.asset)) || '';
    const cb = codeById.get(Number(b.asset)) || '';
    if (ca !== cb) return ca.localeCompare(cb, undefined, { sensitivity: 'base' });
    return String(a.payment_type || '').localeCompare(String(b.payment_type || ''));
  });

  const fmtRefMoney = (v) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtRefQty = (v) => {
    if (v == null || v === '') return '—';
    const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
    if (Number.isNaN(n)) return String(v).trim() || '—';
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const rows = sortedRecords.map((r) => {
    const ymd = toYMD(r.payment_date);
    return {
      ...emptyReturnRow(),
      asset: String(r.asset),
      payment_type: r.payment_type || 'DIVIDENDOS',
      payment_date: '',
      payment_value: '',
      quantity_at_time: (() => {
        if (r.quantity_at_time == null || r.quantity_at_time === '') return '';
        const n = Number(String(r.quantity_at_time).replace(/\s/g, '').replace(',', '.'));
        return Number.isFinite(n) ? n.toFixed(2) : String(r.quantity_at_time).trim();
      })(),
      notes: '',
      refFromLastReturn: {
        paymentDate: ymd ? formatDate(ymd) : '',
        valueLabel: fmtRefMoney(r.payment_value),
        qtyLabel: fmtRefQty(r.quantity_at_time),
      },
    };
  });

  return { rows, monthLabel };
};

/**
 * Skips rows with no asset. Each non-empty row must have payment_date and valid payment_value.
 * @returns {object[] | null}
 */
export const buildReturnsPayloadFromRows = (rows, tradeableAssets) => {
  const assetsById = new Map((tradeableAssets || []).map((a) => [Number(a.id), a]));
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const assetId = r.asset?.trim() ? Number(r.asset) : null;
    if (!assetId) {
      continue;
    }
    if (!assetsById.has(assetId)) {
      toast.error(`Row ${i + 1}: select a tradeable asset.`);
      return null;
    }
    if (!r.payment_date?.trim()) {
      toast.error(`Row ${i + 1}: payment date is required.`);
      return null;
    }
    const pv = parseDecimal(r.payment_value);
    if (pv === null) {
      toast.error(`Row ${i + 1}: payment value is required and must be a valid number.`);
      return null;
    }
    const qty = parseDecimal(r.quantity_at_time);
    if (r.quantity_at_time?.trim() !== '' && qty === null) {
      toast.error(`Row ${i + 1}: invalid quantity.`);
      return null;
    }
    out.push({
      asset: assetId,
      payment_type: r.payment_type || 'DIVIDENDOS',
      payment_date: r.payment_date.trim(),
      payment_value: pv,
      quantity_at_time: qty,
      notes: r.notes?.trim() ? r.notes.trim() : null,
    });
  }
  if (out.length < 1) {
    toast.error('Add at least one complete return line (tradeable asset, date, value).');
    return null;
  }
  return out;
};

/** Format API errors from returns create_many (207 or generic). */
export const formatReturnsCreateManyMessage = (data) => {
  if (!data) return 'Failed to save returns.';
  if (typeof data === 'string') return data;
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors
      .map((e) => (e && e.message ? String(e.message) : JSON.stringify(e)))
      .join(' ');
  }
  if (data.detail) {
    const d = data.detail;
    return Array.isArray(d) ? d.join(' ') : String(d);
  }
  if (data.error) return String(data.error);
  return 'Failed to save returns.';
};
